import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Icon } from '../../common/components/Utilities';
import LeaveAllowanceEstimateResource from '../../common/resource/LeaveAllowanceEstimateResource';

function LeaveAllowance({ userId }) {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allowance, setAllowance] = useState({
    allowance: 0,
    calculation: [],
    method: '',
  });

  const loadData = async () => {
    setLoading(true);

    try {
      const response = await LeaveAllowanceEstimateResource.get(userId);
      setAllowance(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [userId]);

  if (loading) {
    return <div className="text-warning">Calculating leave allowance...</div>;
  }

  let methodString;
  const link = 'https://www.gov.uk/government/publications/calculating-holiday-entitlement-for-workers/how-to-calculate-holiday-entitlement-for-workers-on-different-types-of-contract#';
  let hash;

  switch (allowance.method) {
    case 'start_leave':
      hash = 'calculating-leave-for-somebody-starting-and-leaving-part-way-through-a-leave-year';
      methodString = (
        <>
          <a style={{ fontWeight: 'bold' }} href={`${link}${hash}`}>
            Calculate the leave entitlement for someone starting
            and leaving part-way through a leave year.
          </a>

          <ol>
            <li>Calculate the worker’s full annual leave entitlement</li>
            <li>Work out the proportion of the leave year in employment</li>
            <li>Pro-rate based on the proportion of the year in employment</li>
          </ol>
        </>
      );
      break;

    case 'start':
      hash = 'calculating-leave-for-somebody-starting-part-way-through-a-leave-year';
      methodString = (
        <>
          <a style={{ fontWeight: 'bold' }} href={`${link}${hash}`}>
            Calculate the leave entitlement for someone starting
            part-way through a leave year.
          </a>

          <ol>
            <li>Calculate the worker’s full annual leave entitlement</li>
            <li>Pro-rate based on the number of months started</li>
            <li>
              Where this includes a fraction of a day,
              round up to the next half or whole day (whichever is closer)
            </li>
          </ol>
        </>
      );
      break;

    case 'leave':
      hash = 'calculating-leave-for-somebody-starting-part-way-through-a-leave-year';
      methodString = (
        <>
          <a style={{ fontWeight: 'bold' }} href={`${link}${hash}`}>
            Calculate the leave entitlement for someone leaving
            part-way through a leave year.
          </a>

          <ol>
            <li>Calculate the worker’s full annual leave entitlement</li>
            <li>Work out the proportion of the leave year in employment</li>
            <li>Pro-rate based on the proportion of the year in employment</li>
          </ol>
        </>
      );
      break;

    default:
      methodString = null;
      break;
  }

  return (
    <>
      <div className="text-info">
        {`We think this should be ${allowance.allowance}`}
        <Button variant="link" onClick={() => setShowModal(true)} className="ml-2 p-0" style={{ verticalAlign: 'top' }}><Icon icon="question-circle" /></Button>
      </div>

      <Modal size="lg" show={showModal} onHide={() => setShowModal(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Leave Allowance Calculation</Modal.Title>
        </Modal.Header>

        <div className="alert alert-info m-3" style={{ display: (methodString === null ? 'none' : '') }}>
          <Icon icon="question-circle" variant="fad" size="2x" style={{ position: 'absolute', top: '7px', right: '7px' }} />
          {methodString}
        </div>

        <table className="table mb-0">
          <thead>
            <tr>
              <th>Calculation</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {allowance.calculation.map((row) => (
              <tr>
                <th>
                  <div style={{ fontSize: '1.2em' }}>
                    {row.title}
                  </div>

                  <div className="small text-info">
                    {row.calculation}
                  </div>
                </th>

                <td>
                  {row.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    </>
  );
}

export default LeaveAllowance;
